import { PHONE_MASK } from "@/constants/referring-agency-constant";
import { OptionGroupDef } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  createChooseOneGroups,
  createTextGroups,
  withHeading,
} from "./initial-f2f-utils";

const SURROGATE_PHONE_KEY = "surrogatePhone";
const SURROGATE_PHONE_ERROR = "Enter a 10-digit phone number (xxx) xxx-xxxx";

const YES_NO_OPTIONS = ["Yes", "No"];
const YES_NO_UNSURE_OPTIONS = ["Yes", "No", "Unsure"];

const START_OF_CARE_TIMING_TEXT =
  "Start of care will be initiated within 48 hours of referral or as clinically indicated.";
const ACP_CONSENT_STATEMENT =
  "Advance care planning discussion performed with patient and/or caregiver, including goals of care, advance directives, code status preferences, hospitalization preferences, and surrogate decision maker. Existing living will was reviewed. Questions answered and counseling provided.";
const ACP_MINUTES_PLACEHOLDER = "Minutes, e.g. 16";
const EXISTING_DOCUMENTS_HEADING = "Existing advance directive documents";
const OTHER_DIRECTIVE_HEADING = "Other directive";
const SURROGATE_HEADING = "Surrogate decision-maker";
const DISCHARGE_DATE_OPTION = "Patient discharged from home health services on";

export const ADVANCE_DIRECTIVE_GROUPS: OptionGroupDef[] = [
  {
    key: "patientConsent",
    title: "Patient consent to discuss today",
    chooseOne: true,
    withFreeText: false,
    optionDetailPlaceholders: {
      [ACP_CONSENT_STATEMENT]: ACP_MINUTES_PLACEHOLDER,
    },
    options: [ACP_CONSENT_STATEMENT, "Patient declined"],
  },
  ...withHeading(EXISTING_DOCUMENTS_HEADING, [
    ...createChooseOneGroups(
      {
        livingWill: "Living Will",
        polst: "POLST / MOLST / Portable medical order",
      },
      YES_NO_UNSURE_OPTIONS
    ),
    ...createChooseOneGroups(
      { scannedIntoChart: "Scanned into chart" },
      YES_NO_OPTIONS
    ),
    ...createChooseOneGroups(
      {
        healthCareSurrogate:
          "Health care surrogate / medical power of attorney",
        dnrOrder: "DNR order",
      },
      YES_NO_UNSURE_OPTIONS
    ),
  ]),
  ...withHeading(OTHER_DIRECTIVE_HEADING, [
    {
      key: "otherDirective",
      title: OTHER_DIRECTIVE_HEADING,
      hideTitle: true,
      withFreeText: false,
      optionDetailPlaceholders: { [OTHER_DIRECTIVE_HEADING]: "Describe" },
      options: [OTHER_DIRECTIVE_HEADING],
    },
    ...createChooseOneGroups(
      { copyProvidedToOffice: "Copy provided to office" },
      [...YES_NO_OPTIONS, "Requested today"]
    ),
  ]),
  ...withHeading(SURROGATE_HEADING, [
    ...createTextGroups({
      surrogateName: "Primary decision-maker name",
      surrogateRelationship: "Relationship",
      [SURROGATE_PHONE_KEY]: "Phone",
      alternateDecisionMaker: "Alternate decision-maker",
    }).map((group) =>
      group.key === SURROGATE_PHONE_KEY
        ? {
            ...group,
            inputMask: PHONE_MASK,
            inputMaskError: SURROGATE_PHONE_ERROR,
          }
        : group
    ),
    ...createChooseOneGroups(
      { legallyDesignated: "Legally designated in written document" },
      YES_NO_OPTIONS
    ),
  ]),
];

export const PATIENT_INSTRUCTION_GROUPS: OptionGroupDef[] = [
  {
    key: "patientInstructions",
    title: "Patient instructions",
    withFreeText: false,
    withDetails: false,
    options: [
      "Patient and/or caregiver were instructed on the current plan of care, including medication adherence, safety precautions, and follow-up care. The patient was advised to continue all medications as prescribed and to monitor for any changes in condition.",
      "Fall prevention strategies were reviewed to reduce risk of injury. The importance of infection prevention, including COVID-19 education and vaccination availability, was discussed.",
      "The patient was instructed to follow up with their primary care provider and/or specialist as scheduled, with a recommended follow-up visit in approximately one month.",
      "The patient was advised to call 911 or go to the nearest emergency department for any medical emergency, including worsening symptoms such as chest pain, shortness of breath, or acute changes in condition.",
    ],
  },
];

export type EncounterKind = "face-to-face" | "telehealth";

/** The encounter wording changes with the visit type, as in the reference template. */
export const createFaceToFaceGroups = (
  encounter: EncounterKind
): OptionGroupDef[] => [
  {
    key: "homeboundStatus",
    title: "Homebound status",
    withFreeText: false,
    withDetails: false,
    options: [
      `The patient was evaluated in a ${encounter} encounter and is considered homebound, as leaving the home requires considerable and taxing effort due to functional limitations.`,
    ],
  },
  {
    key: "medicalNecessity",
    title: "Medical necessity",
    withFreeText: false,
    withDetails: false,
    options: [
      "Skilled home health services are medically necessary for ongoing assessment, management, and treatment of the patient's medical condition(s) to prevent deterioration and support recovery.",
    ],
  },
  {
    key: "clinicalAttestation",
    title: "Clinical attestation",
    withFreeText: false,
    withDetails: false,
    options: [
      `I certify that this patient is under my care and that a ${encounter} encounter has occurred in accordance with CMS requirements, or will be documented as required. The patient is considered homebound as defined by Medicare guidelines, requiring considerable and taxing effort to leave the home.`,
      "Based on my evaluation, the ordered Home Health services are reasonable, necessary, and appropriate for the treatment of the patient's medical condition(s). A plan of care will be established, implemented, and periodically reviewed to ensure ongoing medical necessity and effectiveness of services.",
    ],
  },
];

export const FACE_TO_FACE_GROUPS = createFaceToFaceGroups("face-to-face");

export const HOME_HEALTH_ORDER_GROUPS: OptionGroupDef[] = [
  {
    key: "homeHealthServices",
    title: "Orders for Home Health Services include",
    withFreeText: false,
    withDetails: false,
    options: [
      "Skilled Nursing (SN): Evaluation and assessment of patient condition, Medication reconciliation and management, Patient and caregiver education, and Monitoring for complications and disease progression",
      "Physical Therapy (PT): Evaluation and treatment, Interventions to improve mobility, strength, balance, and functional status, and Fall prevention and safety training",
      "Home Health Services (General): Ongoing management and coordination of care and Communication with ordering provider regarding patient status and changes",
      "RN Visit: Initiate Home Health RN visit for comprehensive assessment and plan of care development",
      "Refer for Occupational Therapy",
      "Refer for MSW",
      "Refer for Speech Therapy",
      "Refer for a Caregiver",
      "Refer for LVN",
      "CHHA services: Home Health Aide to provide personal care assistance, including bathing, grooming, dressing, toileting, and assistance with basic activities of daily living.",
      "These services are medically necessary to address the patient's diagnosed condition(s), promote recovery, prevent complications, and reduce the risk of hospitalization or further decline.",
      "I certify that this patient is under my care and that Home Health services are medically necessary based on my evaluation.",
    ],
  },
  {
    key: "dischargeFromHomeHealth",
    title: "Discharge from Home Health",
    withFreeText: false,
    optionDateDetails: [DISCHARGE_DATE_OPTION],
    optionDetailPlaceholders: {
      "Skilled nursing services were provided for": "e.g. 8 weeks",
      "At the time of discharge, patient is alert and oriented to":
        "e.g. person, place and time",
    },
    options: [
      DISCHARGE_DATE_OPTION,
      "Skilled nursing services were provided for",
      "At the time of discharge, patient is alert and oriented to",
      "Patient/caregiver verbalized understanding of discharge instructions and when to notify the physician of any changes in condition.",
    ],
  },
  {
    key: "medicationManagement",
    title: "Medication Management",
    withFreeText: false,
    withDetails: false,
    options: [
      "Medication reconciliation completed. Patient/caregiver demonstrates understanding of medication purpose, dosage, frequency, and potential side effects. No medication discrepancies noted at discharge.",
    ],
  },
  {
    key: "dischargeGoals",
    title: "Goals",
    withFreeText: false,
    optionDetailPlaceholders: {
      "All home health goals have been": "e.g. met",
      "Discharge instructions reviewed with patient/caregiver, including follow-up appointments with":
        "e.g. PCP in one week",
    },
    options: [
      "All home health goals have been",
      "Discharge instructions reviewed with patient/caregiver, including follow-up appointments with",
      "Patient/caregiver verbalized understanding and agreement with discharge plan. Physician notified of discharge from home health services. Patient discharged in stable condition.",
    ],
  },
];

export const START_OF_CARE_GROUPS: OptionGroupDef[] = [
  {
    key: "skilledNursingServices",
    title: "Skilled nursing services to include",
    withFreeText: false,
    withDetails: false,
    options: [
      "Skilled assessment and ongoing evaluation of the patient's clinical status, including changes in condition and response to treatment.",
      "Assessment of Activities of Daily Living (ADLs) as they relate to disease process, functional limitations, and safety.",
      "Medication reconciliation, management, and monitoring for effectiveness, adherence, and adverse reactions.",
      "Patient and caregiver education on disease processes, medications, and self-management strategies.",
      "Assessment of home environment for safety risks and identification of potential hazards to prevent exacerbation or injury.",
      "Skilled observation and monitoring to prevent complications and reduce risk of hospitalization.",
      "Coordination of care with interdisciplinary team, including therapy services.",
      "Monitoring patient's response to therapy interventions, including gait and exercise programs (in coordination with Physical Therapy).",
      "Education on safe and proper use of assistive devices.",
    ],
  },
  {
    key: "skilledNeed",
    title: "Skilled need / medical necessity",
    withFreeText: false,
    chooseOne: true,
    options: [
      "The patient requires skilled nursing services due to medical condition(s), functional limitations, and the need for ongoing skilled clinical assessment, monitoring, and education that cannot be safely or effectively performed by non-skilled personnel.",
    ],
  },
  {
    key: "startOfCareTiming",
    title: "Start of care timing",
    withDetails: false,
    options: [START_OF_CARE_TIMING_TEXT],
  },
];
